import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = slug || 'all';
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(1);
  }, [activeCategory]);

  const filteredComics: Comic[] = useMemo(() => {
    if (activeCategory === 'all') return comics;
    return comics.filter((c) => c.categories.includes(activeCategory));
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pagedComics = filteredComics.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const currentCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';
  const categoryDescriptionMap: Record<string, string> = {
    all: '浏览全部韩国漫画资源，涵盖恋爱、奇幻、动作、搞笑、悬疑、剧情、日常、古风、恐怖等多种类型。',
    romance: '精选韩国恋爱漫画，从甜蜜校园到虐心虐恋，感受浪漫的无限可能。',
    fantasy: '奇幻韩国漫画推荐，魔法世界、龙与剑、异世界冒险等你来探索。',
    action: '热血韩国动作漫画，激烈的战斗场面，紧张刺激的故事剧情。',
    comedy: '搞笑韩国漫画精选，幽默风趣的角色和爆笑情节，让你开怀大笑。',
    thriller: '悬疑韩国漫画推荐，烧脑推理、犯罪追踪，每一页都扣人心弦。',
    drama: '韩国剧情漫画精选，深刻的人物刻画，引人入胜的故事发展。',
    daily: '日常韩国漫画推荐，温馨治愈的日常生活，平凡中的温暖与感动。',
    historical: '古风韩国漫画精选，穿越古代王朝，体验历史中的爱恨情仇。',
    horror: '韩国恐怖漫画推荐，惊悚灵异、细思极恐，挑战你的胆量极限。',
  };

  const seoTitle = activeCategory === 'all'
    ? '分类浏览 - 全部韩国漫画 | 韩漫网'
    : `${currentCategoryName}漫画 - 韩国${currentCategoryName}漫画推荐 | 韩漫网`;

  const seoDescription = activeCategory === 'all'
    ? '韩漫网分类浏览页面，查看全部韩国漫画资源。涵盖恋爱、奇幻、动作、搞笑、悬疑等多种类型，每日更新高清韩国漫画大全。'
    : `韩漫网${currentCategoryName}漫画分类，精选韩国${currentCategoryName}漫画推荐。高清画质免费在线观看，每日更新。${categoryDescriptionMap[activeCategory] || ''}`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={`韩漫网,${currentCategoryName}漫画,韩国${currentCategoryName}漫画,在线${currentCategoryName}漫画,${currentCategoryName}韩漫`}
        canonicalPath={`/category/${activeCategory}`}
        ogType="website"
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: currentCategoryName },
              ]}
            />
            <div className="pb-6">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                {currentCategoryName}漫画
              </h1>
              <p className="text-sm text-foreground-400 mt-1">
                {categoryDescriptionMap[activeCategory] || ''}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400">暂无该分类的漫画</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {pagedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center mt-10 gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}